import { useState, useEffect } from 'react';
import { format, subDays, subYears } from 'date-fns';

import { getShortAgents, getAgentAnalytics } from '../../../../../api';
import { useFetch, useModal } from '../../../../../hooks';
import { getStoredSettings, saveSettings } from '../../../../../utilities';
import { useAuth } from '../../../../../shared/User';
import { AnalyticsPeriod } from '../../../const';
import type { AnalyticsMetric, MetricCardType } from '../../../const';

export type AnalyticsFormProps = {
  agentId: string;
  period: AnalyticsPeriod;
};

const STORAGE_KEY = 'analytics_settings';

const defaultFilter = {
  agentId: '',
  period: AnalyticsPeriod.TODAY,
};

export const useAnalytics = () => {
  const { isOpened, onClose, onOpen } = useModal();
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [chartModalData, setChartModalData] = useState<{
    title: string;
    data: Array<{
      bucket: string;
      value: string;
      conversationNumber?: number | null;
      metricType?: string;
    }>;
    unit?: string | { minute: string; seconds: string };
    scale?: string | null;
    chartType?: AnalyticsMetric | MetricCardType;
  } | null>(null);
  const { organization_id } = useAuth();
  const storedSettings = getStoredSettings(STORAGE_KEY);

  const [formId, setFormId] = useState(1);
  const [form, setForm] = useState<AnalyticsFormProps | null>({
    ...(storedSettings?.filters ?? defaultFilter),
  });
  const { loading: isAgentsLoading, data: agentsOptions } = useFetch(
    async () => {
      const data = await getShortAgents();

      return (data ?? []).map(d => ({
        value: d.id ?? '',
        label: d.title,
      }));
    },
    [organization_id],
    {
      onSuccess: result => {
        if (result && result.length > 0) {
          const shouldResetAgent =
            !storedSettings?.filters?.agentId ||
            !result.some(agent => agent.value === form?.agentId);

          if (shouldResetAgent) {
            setForm(prev =>
              prev ? { ...prev, agentId: result[0].value } : null
            );
            setFormId(f => f + 1);
          }
        }
      },
    }
  );

  useEffect(() => {
    if (form) {
      saveSettings(STORAGE_KEY, form, null);
    }
  }, [form]);

  const {
    loading: isSubmitting,
    data,
    error,
  } = useFetch<any>(
    async _signal => {
      if (!form?.agentId) return null;

      const now = new Date();
      let from: string, to: string;

      switch (form?.period) {
        case AnalyticsPeriod.TODAY:
          from = format(subDays(now, 1), "yyyy-MM-dd'T'HH:mm:ss.SSS");
          to = format(now, "yyyy-MM-dd'T'HH:mm:ss.SSS");
          break;
        case AnalyticsPeriod.WEEK:
          from = format(subDays(now, 7), "yyyy-MM-dd'T'HH:mm:ss.SSS");
          to = format(now, "yyyy-MM-dd'T'HH:mm:ss.SSS");
          break;
        case AnalyticsPeriod.MONTH:
          from = format(subDays(now, 30), "yyyy-MM-dd'T'HH:mm:ss.SSS");
          to = format(now, "yyyy-MM-dd'T'HH:mm:ss.SSS");
          break;
        case AnalyticsPeriod.YEAR:
          from = format(subYears(now, 1), "yyyy-MM-dd'T'HH:mm:ss.SSS");
          to = format(now, "yyyy-MM-dd'T'HH:mm:ss.SSS");
          break;
        default:
          from = format(subDays(now, 1), "yyyy-MM-dd'T'HH:mm:ss.SSS");
          to = format(now, "yyyy-MM-dd'T'HH:mm:ss.SSS");
      }

      return await getAgentAnalytics({
        agentId: form?.agentId,
        from,
        to,
      });
    },
    [form?.period, form?.agentId]
  );

  const openChartModal = (
    title: string,
    data: Array<{
      bucket: string;
      value: string;
      conversationNumber?: number | null;
      metricType?: string;
    }>,
    unit?: string | { minute: string; seconds: string },
    scale?: string | null,
    chartType?: AnalyticsMetric | MetricCardType
  ) => {
    setChartModalData({ title, data, unit, scale, chartType });
    setIsChartModalOpen(true);
  };

  const closeChartModal = () => {
    setIsChartModalOpen(false);
    setChartModalData(null);
  };

  return {
    form,
    onChange: setForm,
    agentsOptions,
    data,
    error,
    isSubmitting,
    isAgentsLoading,
    isStatusModalOpen,
    setIsStatusModalOpen,
    isChartModalOpen,
    chartModalData,
    openChartModal,
    closeChartModal,
    formId,
    setFormId,
    isOpened,
    onClose,
    onOpen,
  };
};
